import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { 
  Layers, 
  Filter, 
  CheckSquare, 
  Loader2,
  ChevronLeft,
  ChevronRight,
  AlertCircle
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BulkCategoryDepartmentModal = ({ open, onClose, categories, departments, onComplete }) => {
  const [activeTab, setActiveTab] = useState("filter");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [orphanData, setOrphanData] = useState(null);
  
  // Filter-based assignment
  const [filterType, setFilterType] = useState("supplier");
  const [filterValue, setFilterValue] = useState("");
  const [includeUncategorized, setIncludeUncategorized] = useState(false);
  const [includeOrphanCategories, setIncludeOrphanCategories] = useState(false);
  const [selectedOrphanCategory, setSelectedOrphanCategory] = useState("");
  const [targetCategory, setTargetCategory] = useState("");
  const [targetDepartment, setTargetDepartment] = useState("");
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [previewCount, setPreviewCount] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Manual selection
  const [searchQuery, setSearchQuery] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [orphanCategoryFilter, setOrphanCategoryFilter] = useState("");
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [productsLoading, setProductsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const pageSize = 50;
  
  // Unique values for filters
  const [suppliers, setSuppliers] = useState([]);
  const [existingCategories, setExistingCategories] = useState([]);

  useEffect(() => {
    if (open) {
      fetchStats();
      fetchFilterOptions();
      fetchOrphanCategories();
    }
  }, [open]);

  useEffect(() => {
    if (activeTab === "select" && open) {
      fetchProducts();
    }
  }, [activeTab, searchQuery, supplierFilter, orphanCategoryFilter, page, open]);

  // Update filtered departments when target category changes
  useEffect(() => {
    if (targetCategory) {
      const cat = categories.find(c => c.name === targetCategory);
      if (cat) {
        const depts = departments.filter(d => d.category_id === cat.id);
        setFilteredDepartments(depts);
      } else {
        setFilteredDepartments([]);
      }
    } else {
      setFilteredDepartments([]);
    }
    setTargetDepartment("");
  }, [targetCategory, categories, departments]);

  // Auto-preview count when filter changes
  useEffect(() => {
    if (filterValue || includeUncategorized) {
      fetchPreviewCount();
    } else {
      setPreviewCount(null);
    }
  }, [filterType, filterValue, includeUncategorized]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/products/category-stats`);
      setStats(res.data);
    } catch (err) {
      console.error("Error fetching category stats:", err);
    }
  };

  const fetchOrphanCategories = async () => {
    try {
      const res = await axios.get(`${API}/products/orphan-categories`);
      setOrphanData(res.data);
    } catch (err) {
      console.error("Error fetching orphan categories:", err);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      // Get unique suppliers
      const suppRes = await axios.get(`${API}/suppliers`);
      setSuppliers(suppRes.data || []);
      
      // Get existing categories from products (for filtering)
      setExistingCategories(categories.map(c => c.name));
    } catch (err) {
      console.error("Error fetching filter options:", err);
    }
  };

  const fetchPreviewCount = async () => {
    setPreviewLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType && filterValue) {
        params.append("filter_type", filterType);
        params.append("filter_value", filterValue);
      }
      if (includeUncategorized) {
        params.append("uncategorized_only", "true");
      }
      
      const res = await axios.get(`${API}/products/bulk-category-preview?${params}`);
      setPreviewCount(res.data.count);
    } catch (err) {
      console.error("Error fetching preview:", err);
      setPreviewCount(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const fetchProducts = async () => {
    setProductsLoading(true);
    try {
      let res;
      
      // If we have a search query, use search endpoint
      if (searchQuery) {
        const params = new URLSearchParams({
          q: searchQuery,
          limit: "200"
        });
        res = await axios.get(`${API}/products/search?${params}`);
        let results = res.data || [];
        
        // Filter by supplier if selected
        if (supplierFilter) {
          results = results.filter(p => p.supplier === supplierFilter);
        }
        
        // Filter by orphan category if selected
        if (orphanCategoryFilter) {
          results = results.filter(p => p.category === orphanCategoryFilter);
        }
        
        // Paginate the results
        const startIndex = (page - 1) * pageSize;
        setProducts(results.slice(startIndex, startIndex + pageSize));
        setTotalProducts(results.length);
      } 
      // Use paginated endpoint for supplier filter or default browsing
      else {
        const params = new URLSearchParams({
          skip: ((page - 1) * pageSize).toString(),
          limit: pageSize.toString()
        });
        
        if (supplierFilter) {
          params.append("supplier", supplierFilter);
        }
        
        // Filter by orphan category
        if (orphanCategoryFilter) {
          params.append("category", orphanCategoryFilter);
        }
        
        res = await axios.get(`${API}/products/paginated?${params}`);
        const data = res.data;
        console.log('[BulkCategory] API Response:', { 
          total: data.total, 
          productsCount: data.products?.length,
          firstProduct: data.products?.[0],
          category: orphanCategoryFilter,
          supplier: supplierFilter,
          params: params.toString()
        });
        if (data.products && data.products.length > 0) {
          console.log('[BulkCategory] First 3 products:', data.products.slice(0, 3).map(p => ({ name: p.name, category: p.category })));
        }
        setProducts(data.products || []);
        setTotalProducts(data.total || 0);
      }
    } catch (err) {
      console.error("Error fetching products:", err);
      setProducts([]);
    } finally {
      setProductsLoading(false);
    }
  };

  const handleFilterAssign = async () => {
    if (!targetCategory) {
      toast.error("Please select a target category");
      return;
    }
    if (!filterValue && !includeUncategorized) {
      toast.error("Please select a filter or check 'Uncategorized only'");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/products/bulk-assign-category`, {
        filter_type: filterValue ? filterType : null,
        filter_value: filterValue || null,
        uncategorized_only: includeUncategorized,
        category: targetCategory,
        department: targetDepartment || null
      });
      
      toast.success(res.data.message);
      fetchStats();
      setFilterValue("");
      setIncludeUncategorized(false);
      setPreviewCount(null);
      onComplete?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 
                       Array.isArray(detail) ? detail.map(e => e.msg).join(', ') :
                       "Failed to assign category";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAssign = async () => {
    if (selectedProducts.size === 0 || !targetCategory) {
      toast.error("Please select products and a target category");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/products/bulk-assign-category-selected`, {
        product_ids: Array.from(selectedProducts),
        category: targetCategory,
        department: targetDepartment || null
      });
      
      toast.success(res.data.message);
      fetchStats();
      setSelectedProducts(new Set());
      fetchProducts();
      onComplete?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 
                       Array.isArray(detail) ? detail.map(e => e.msg).join(', ') :
                       "Failed to assign category";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const getProductKey = (p) => p.id || p.plu_code;

  const selectAllOnPage = () => {
    const newSet = new Set(selectedProducts);
    products.forEach(p => newSet.add(getProductKey(p)));
    setSelectedProducts(newSet);
  };

  const deselectAll = () => {
    setSelectedProducts(new Set());
  };

  const getFilterOptions = () => {
    switch (filterType) {
      case "category":
        return existingCategories;
      case "supplier":
        return suppliers.map(s => s.name);
      case "name_contains":
        return []; // Text input instead
      default:
        return [];
    }
  };

  const totalPages = Math.ceil(totalProducts / pageSize);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-purple-600" />
            Bulk Assign Category & Department
          </DialogTitle>
        </DialogHeader>

        {/* Category Stats & Orphan Warnings - collapsible */}
        {activeTab !== "select" && (
          <>
            {/* Category Stats Summary */}
            {stats && (
              <div className="bg-gray-50 rounded-lg p-2 mb-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium">Category Distribution</span>
                  <span className="text-xs text-orange-600 font-medium">
                    <AlertCircle className="h-3 w-3 inline mr-1" />
                    {stats.uncategorized_count} without category
                  </span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {stats.top_categories?.slice(0, 10).map(cat => (
                    <Badge key={cat.name} variant="outline" className="text-[10px]">
                      {cat.name}: {cat.count}
                    </Badge>
                  ))}
                  {stats.category_count > 10 && (
                    <Badge variant="outline" className="text-[10px] text-gray-400">
                      +{stats.category_count - 10} more
                    </Badge>
                  )}
                </div>
              </div>
            )}

            {/* Orphan Categories Warning */}
            {orphanData && orphanData.orphan_categories?.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-2 mb-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-red-800 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Orphan Categories ({orphanData.total_orphan_products} products)
                  </span>
                  <span className="text-[10px] text-red-600">
                    Click a badge to filter
                  </span>
                </div>
                <div className="flex flex-wrap gap-1 max-h-[80px] overflow-y-auto">
                  {orphanData.orphan_categories.map(cat => (
                    <Badge 
                      key={cat.name} 
                      variant="outline" 
                      className="text-[10px] bg-red-100 text-red-800 border-red-300 cursor-pointer hover:bg-red-200"
                      onClick={() => {
                        setActiveTab("select");
                        setOrphanCategoryFilter(cat.name);
                        setPage(1);
                      }}
                    >
                      {cat.name}: {cat.count}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="filter" className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Assign by Filter
            </TabsTrigger>
            <TabsTrigger value="select" className="flex items-center gap-2">
              <CheckSquare className="h-4 w-4" />
              Select Products
            </TabsTrigger>
          </TabsList>

          {/* Filter-based Assignment Tab */}
          <TabsContent value="filter" className="space-y-4 mt-4 overflow-y-auto">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h4 className="font-medium text-purple-900 mb-3">
                Assign category to multiple products at once
              </h4>
              
              {/* Uncategorized only checkbox */}
              <div className="flex items-center gap-2 mb-4 p-2 bg-orange-50 border border-orange-200 rounded">
                <Checkbox
                  id="uncategorizedOnly"
                  checked={includeUncategorized}
                  onCheckedChange={setIncludeUncategorized}
                />
                <Label htmlFor="uncategorizedOnly" className="text-sm cursor-pointer text-orange-800">
                  Only update products without a category ({stats?.uncategorized_count || 0} products)
                </Label>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <Label>Filter By (Optional)</Label>
                  <Select value={filterType} onValueChange={(v) => { setFilterType(v); setFilterValue(""); }}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="supplier">Supplier</SelectItem>
                      <SelectItem value="category">Current Category</SelectItem>
                      <SelectItem value="name_contains">Name Contains</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Filter Value</Label>
                  {filterType === "name_contains" ? (
                    <Input
                      placeholder="Enter text to match..."
                      value={filterValue}
                      onChange={(e) => setFilterValue(e.target.value)}
                    />
                  ) : (
                    <Select value={filterValue} onValueChange={setFilterValue}>
                      <SelectTrigger>
                        <SelectValue placeholder={`Select ${filterType}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {[...getFilterOptions()].sort((a, b) => a.localeCompare(b)).map(opt => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>

              {/* Preview count */}
              {(filterValue || includeUncategorized) && (
                <div className="mb-4 p-2 bg-white border rounded text-center">
                  {previewLoading ? (
                    <span className="text-gray-500">Counting...</span>
                  ) : previewCount !== null ? (
                    <span className="font-medium text-purple-700">
                      {previewCount} products will be updated
                    </span>
                  ) : null}
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Assign to Category *</Label>
                  <Select value={targetCategory} onValueChange={setTargetCategory}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {[...categories].sort((a, b) => a.name.localeCompare(b.name)).map(cat => (
                        <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Assign to Department</Label>
                  <Select 
                    value={targetDepartment} 
                    onValueChange={setTargetDepartment}
                    disabled={!targetCategory || filteredDepartments.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={
                        !targetCategory ? "Select category first" :
                        filteredDepartments.length === 0 ? "No departments" :
                        "Select department (optional)"
                      } />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">-- No Department --</SelectItem>
                      {[...filteredDepartments].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(dept => (
                        <SelectItem key={dept.id} value={dept.name}>{dept.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <Button 
                className="mt-4 w-full bg-purple-600 hover:bg-purple-700" 
                onClick={handleFilterAssign}
                disabled={loading || !targetCategory || (!filterValue && !includeUncategorized)}
              >
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Layers className="h-4 w-4 mr-2" />}
                Assign {previewCount || 0} Products to {targetCategory || "..."}
              </Button>
            </div>
            
            <div className="text-sm text-gray-600 space-y-1">
              <p><strong>Examples:</strong></p>
              <p>• Check "Uncategorized only" + Select "GROCERIES" → All products without category get assigned to GROCERIES</p>
              <p>• Select Supplier "AGRINET" + Category "HARDWARE" → All Agrinet products go to HARDWARE</p>
              <p>• Name contains "CEMENT" + Category "BUILDING" → All cement products go to BUILDING</p>
            </div>
          </TabsContent>

          {/* Manual Selection Tab */}
          <TabsContent value="select" className="flex-1 flex flex-col min-h-0 mt-2">
            {/* Filters Row */}
            <div className="flex items-center gap-3 mb-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-medium text-blue-900 whitespace-nowrap">Supplier:</Label>
                <Select 
                  value={supplierFilter} 
                  onValueChange={(v) => { setSupplierFilter(v === "__all__" ? "" : v); setPage(1); }}
                >
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="All Suppliers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">-- All Suppliers --</SelectItem>
                    {[...suppliers].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(s => (
                      <SelectItem key={s.id || s.name} value={s.name}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Orphan Category Filter */}
              {orphanData && orphanData.orphan_categories?.length > 0 && (
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-medium text-red-800 whitespace-nowrap">Orphan Cat:</Label>
                  <Select 
                    value={orphanCategoryFilter} 
                    onValueChange={(v) => { setOrphanCategoryFilter(v === "__all__" ? "" : v); setPage(1); }}
                  >
                    <SelectTrigger className="w-40 border-red-300 bg-red-50">
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">-- Any Category --</SelectItem>
                      {[...orphanData.orphan_categories].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(cat => (
                        <SelectItem key={cat.name} value={cat.name}>
                          {cat.name} ({cat.count})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {(supplierFilter || orphanCategoryFilter) && (
                <div className="flex gap-1 ml-auto">
                  {supplierFilter && (
                    <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                      {supplierFilter}
                    </Badge>
                  )}
                  {orphanCategoryFilter && (
                    <Badge variant="secondary" className="bg-red-100 text-red-800">
                      {orphanCategoryFilter}
                    </Badge>
                  )}
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => { setSupplierFilter(""); setOrphanCategoryFilter(""); setPage(1); }}
                    className="h-5 px-1 text-gray-500"
                  >
                    Clear
                  </Button>
                </div>
              )}
            </div>

            {/* Search and Category/Department Row */}
            <div className="flex items-center gap-2 mb-2">
              <Input
                placeholder="Search products by name or PLU..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                className="flex-1"
              />
              <Select value={targetCategory} onValueChange={setTargetCategory}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  {[...categories].sort((a, b) => a.name.localeCompare(b.name)).map(cat => (
                    <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select 
                value={targetDepartment} 
                onValueChange={(v) => setTargetDepartment(v === "__none__" ? "" : v)}
                disabled={!targetCategory || filteredDepartments.length === 0}
              >
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">-- None --</SelectItem>
                  {[...filteredDepartments].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(dept => (
                    <SelectItem key={dept.id} value={dept.name}>{dept.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={selectAllOnPage}>
                  Select Page
                </Button>
                <Button size="sm" variant="outline" onClick={deselectAll}>
                  Clear
                </Button>
                <span className="text-sm text-gray-600">
                  {selectedProducts.size} selected
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm">
                  Page {page} of {totalPages || 1}
                </span>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto border rounded-lg">
              {productsLoading ? (
                <div className="flex items-center justify-center h-40">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : products.length === 0 ? (
                <div className="flex items-center justify-center h-40 text-gray-400">
                  {orphanCategoryFilter 
                    ? `No products found with category "${orphanCategoryFilter}"` 
                    : supplierFilter 
                      ? `No products found for supplier "${supplierFilter}"` 
                      : "No products found"}
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="w-10 p-2"></th>
                      <th className="text-left p-2">Product</th>
                      <th className="text-left p-2 w-32">Supplier</th>
                      <th className="text-left p-2">Category</th>
                      <th className="text-left p-2">Department</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product, index) => {
                      const pk = getProductKey(product) || `idx-${index}`;
                      return (
                      <tr 
                        key={pk} 
                        className={`border-b hover:bg-gray-50 cursor-pointer ${selectedProducts.has(pk) ? 'bg-purple-50' : ''}`}
                        onClick={() => toggleProductSelection(pk)}
                      >
                        <td className="p-2 text-center">
                          <Checkbox 
                            checked={selectedProducts.has(pk)}
                            onCheckedChange={() => toggleProductSelection(pk)}
                          />
                        </td>
                        <td className="p-2">
                          <div className="font-medium">{product.name || 'No name'}</div>
                          <div className="text-xs text-gray-500">{product.plu_code || 'No PLU'}</div>
                        </td>
                        <td className="p-2 text-xs text-gray-600">
                          {product.supplier || '-'}
                        </td>
                        <td className="p-2">
                          {product.category ? (
                            <Badge variant="outline">{product.category}</Badge>
                          ) : (
                            <span className="text-orange-500 text-xs">Uncategorized</span>
                          )}
                        </td>
                        <td className="p-2 text-gray-600 text-xs">
                          {product.department || '-'}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            <Button 
              className="mt-2 w-full bg-purple-600 hover:bg-purple-700 shrink-0" 
              onClick={handleSelectAssign}
              disabled={loading || selectedProducts.size === 0 || !targetCategory}
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Layers className="h-4 w-4 mr-2" />}
              Assign {selectedProducts.size} Products to {targetCategory || "..."}
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
